import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, ArrowLeft, CheckCircle2, XCircle, AlertTriangle, KeyRound, LogOut, ScanLine } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ScanPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [stats, setStats] = useState(null);
  const [result, setResult] = useState(null); // {status, registration}
  const [manualInput, setManualInput] = useState("");
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ value: "", at: 0 });

  useEffect(() => {
    if (!user) nav("/admin/connexion");
    else if (user.role !== "admin") nav("/");
  }, [user, nav]);

  useEffect(() => {
    api.get("/admin/events").then((r) => {
      setEvents(r.data);
      const featured = r.data.find((e) => e.is_featured);
      if (featured) setEventId(featured.id);
      else if (r.data.length > 0) setEventId(r.data[0].id);
    });
  }, []);

  // Refresh live stats every 5s
  useEffect(() => {
    if (!eventId) return;
    const fetchStats = () => api.get(`/admin/events/${eventId}/live-stats`).then((r) => setStats(r.data)).catch(() => {});
    fetchStats();
    const id = setInterval(fetchStats, 5000);
    return () => clearInterval(id);
  }, [eventId, result]);

  const submitScan = async (raw) => {
    const now = Date.now();
    // Anti-spam : ignore le même payload dans les 1.5s
    if (raw === lastScanRef.current.value && now - lastScanRef.current.at < 1500) return;
    lastScanRef.current = { value: raw, at: now };
    try {
      const r = await api.post("/admin/scan", { payload: raw, event_id: eventId || null });
      setResult(r.data);
      // beep audio
      try { new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=").play(); } catch (e) {/* ignore */}
    } catch (e) {
      setResult({ status: "invalid", reason: e?.response?.data?.detail || "Erreur" });
    }
  };

  const startScanner = async () => {
    if (scannerActive) return;
    try {
      const html5Qrcode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5Qrcode;
      await html5Qrcode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => submitScan(decoded),
        () => {} // ignore non-decode frames
      );
      setScannerActive(true);
    } catch (e) {
      setResult({ status: "invalid", reason: "Caméra inaccessible : " + (e?.message || e) });
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && scannerActive) {
      try { await scannerRef.current.stop(); } catch (e) {/* ignore */}
      try { scannerRef.current.clear(); } catch (e) {/* ignore */}
      scannerRef.current = null;
      setScannerActive(false);
    }
  };

  useEffect(() => () => { stopScanner(); }, []); // eslint-disable-line

  const manualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      submitScan(manualInput.trim());
      setManualInput("");
    }
  };

  const STATUS_CONF = {
    valid: { c: "bg-emerald-500", t: "✅ PARTICIPANT VALIDÉ", icon: CheckCircle2, msg: "Accès autorisé — bienvenue." },
    already_used: { c: "bg-amber-500", t: "⚠️ DÉJÀ VALIDÉ", icon: AlertTriangle, msg: "Ce participant est déjà passé." },
    revoked: { c: "bg-red-600", t: "❌ INSCRIPTION RÉVOQUÉE", icon: XCircle, msg: "Cette inscription a été annulée." },
    wrong_event: { c: "bg-orange-600", t: "⚠️ MAUVAIS ÉVÉNEMENT", icon: AlertTriangle, msg: "Ce QR concerne un autre événement." },
    invalid: { c: "bg-red-600", t: "❌ QR CODE INVALIDE", icon: XCircle, msg: "QR non reconnu." },
  };
  const conf = result ? STATUS_CONF[result.status] || STATUS_CONF.invalid : null;

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="scan-page">
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 grid place-items-center border border-white/15 hover:border-gold hover:text-gold rounded-sm transition-colors" data-testid="back-admin">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">Sécurité événementielle</div>
            <div className="font-serif text-lg leading-tight">Scan d'accès</div>
          </div>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="text-xs text-white/60 hover:text-gold flex items-center gap-1.5">
          <LogOut className="w-3.5 h-3.5" /> Quitter
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Event selector */}
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <label className="block text-[0.65rem] uppercase tracking-[0.2em] text-white/60 mb-2">Événement contrôlé</label>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)}
                  data-testid="scan-event-select"
                  className="w-full bg-ink border border-white/15 rounded-sm px-3 py-2.5 text-sm focus:border-gold outline-none">
            {events.map((e) => (
              <option key={e.id} value={e.id} className="text-ink">{e.title}</option>
            ))}
          </select>
        </div>

        {/* Live stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3" data-testid="live-stats">
            <Stat label="Inscrits" value={stats.total_registered} />
            <Stat label="Présents" value={stats.total_present} highlight />
            <Stat label="Annulés" value={stats.total_revoked} />
          </div>
        )}

        {/* Scanner area */}
        <div className="bg-white/5 border border-white/10 rounded-sm overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
            <div className="flex items-center gap-2 text-sm">
              <ScanLine className="w-4 h-4 text-gold" />
              {scannerActive ? "Caméra active" : "Caméra en attente"}
            </div>
            {scannerActive ? (
              <button onClick={stopScanner} data-testid="scan-stop"
                      className="px-3 py-1.5 text-xs border border-white/20 hover:border-gold rounded-sm">Arrêter</button>
            ) : (
              <button onClick={startScanner} data-testid="scan-start"
                      className="px-4 py-2 bg-gold text-ink text-xs font-bold rounded-sm hover:bg-gold/90 flex items-center gap-2">
                <Camera className="w-3.5 h-3.5" /> Démarrer la caméra
              </button>
            )}
          </div>
          <div id="qr-reader" className={`w-full ${scannerActive ? "min-h-[300px]" : "min-h-0"} bg-black`} />
        </div>

        {/* Manual fallback */}
        <form onSubmit={manualSubmit} className="bg-white/5 border border-white/10 rounded-sm p-4 flex items-center gap-2"
              data-testid="manual-scan-form">
          <KeyRound className="w-4 h-4 text-gold shrink-0" />
          <input type="text" value={manualInput} onChange={(e) => setManualInput(e.target.value)}
                 placeholder="Saisir manuellement le N° d'inscription (FUR-26-XXXXXXXX)"
                 data-testid="manual-scan-input"
                 className="flex-1 bg-transparent border-b border-white/15 px-2 py-1.5 text-sm focus:border-gold outline-none" />
          <button type="submit" data-testid="manual-scan-submit"
                  className="px-4 py-1.5 bg-gold text-ink text-xs font-bold rounded-sm hover:bg-gold/90">Valider</button>
        </form>

        {/* Result */}
        {result && conf && (
          <div data-testid={`scan-result-${result.status}`}
               className={`${conf.c} text-white p-6 sm:p-8 rounded-sm shadow-2xl animate-fade-up`}>
            <div className="text-center">
              <conf.icon className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3" />
              <div className="text-3xl sm:text-5xl font-extrabold tracking-wide leading-tight">{conf.t}</div>
              <div className="text-sm sm:text-base opacity-90 mt-2">{conf.msg}</div>
            </div>
            {result.registration && (
              <div className="mt-6 pt-5 border-t border-white/30 text-center">
                <div className="text-[0.65rem] uppercase tracking-[0.3em] opacity-75">Participant</div>
                <div className="font-serif text-2xl sm:text-4xl mt-1 leading-tight">
                  {result.registration.first_name} {result.registration.last_name}
                </div>
                {result.registration.checked_in_at && (
                  <div className="mt-4 inline-block bg-white/15 px-4 py-2 rounded-sm">
                    <div className="text-[0.6rem] uppercase tracking-[0.25em] opacity-80">
                      {result.status === "already_used" ? "Premier passage" : "Heure d'entrée"}
                    </div>
                    <div className="font-mono text-xl sm:text-2xl mt-0.5">
                      {new Date(result.registration.checked_in_at).toLocaleString("fr-FR", {
                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                  <Field l="N°" v={result.registration.registration_number} mono />
                  {result.registration.selected_stage && <Field l="Étape" v={result.registration.selected_stage} />}
                  {result.registration.city && <Field l="Ville" v={result.registration.city} />}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-sm border ${highlight ? "bg-gold/15 border-gold/40" : "bg-white/5 border-white/10"}`}>
      <div className="text-[0.55rem] uppercase tracking-[0.2em] text-white/70">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${highlight ? "text-gold" : "text-white"}`}>{value}</div>
    </div>
  );
}
function Field({ l, v, mono }) {
  return (
    <div>
      <div className="text-[0.55rem] uppercase tracking-[0.2em] opacity-70">{l}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-sm" : "text-sm"}`}>{v}</div>
    </div>
  );
}
